import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import CodeBlockWithActions from './CodeBlockWithActions'
import ToolCallCollapsed from './ToolCallCollapsed'
import ToolCallExpanded from './ToolCallExpanded'

const useStyles = makeStyles(theme => ({
  message: {
    display: 'flex',
    flexDirection: 'column'
  },
  userMessage: {
    alignItems: 'flex-end',
    marginBottom: theme.spacing(1.5)
  },
  assistantMessage: {
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1.5)
  },
  messageBubble: {
    maxWidth: '80%',
    wordWrap: 'break-word'
  },
  userBubble: {
    padding: theme.spacing(1, 1.5),
    borderRadius: '8px',
    backgroundColor: 'var(--color-primary-50)',
    color: 'var(--text-color-main)',
    border: '1px solid var(--color-primary-200)'
  },
  assistantBubble: {
    padding: 0
  },
  content: {
    fontSize: 'var(--font-size-sm)',
    lineHeight: 1.6,
    color: 'var(--text-color-main)',
    '& p': {
      margin: '0 0 8px 0',
      '&:last-child': {
        margin: 0
      }
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontSize: 'calc(var(--font-size-sm) * 1.15)',
      fontWeight: 600,
      margin: '12px 0 8px 0',
      color: 'var(--text-color-main)',
      lineHeight: 1.4,
      '&:first-child': {
        marginTop: 0
      }
    },
    '& hr': {
      border: 'none',
      borderTop: '1px solid var(--color-neutral-600)',
      opacity: 0.3,
      margin: '12px 0'
    },
    '& ul, & ol': {
      margin: '0 0 8px 0',
      paddingLeft: '24px',
      '& li': {
        margin: '4px 0',
        lineHeight: 1.6
      }
    },
    '& blockquote': {
      borderLeft: '3px solid var(--color-neutral-600)',
      paddingLeft: theme.spacing(1.5),
      margin: '8px 0',
      color: 'var(--color-neutral-400)',
      fontStyle: 'italic'
    },
    '& strong': {
      fontWeight: 600,
      color: 'var(--text-color-main)'
    },
    '& em': {
      fontStyle: 'italic'
    },
    '& del, & s': {
      textDecoration: 'none',
      opacity: 0.7
    },
    '& a': {
      color: 'var(--color-primary-500)',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    '& code': {
      backgroundColor: 'var(--surface-bg-color-d1)',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '11px',
      fontFamily: 'var(--font-family-monospace)',
      color: 'var(--color-primary-600)'
    },
    '& pre': {
      border: 'var(--border-neutral-thin)',
      backgroundColor: 'var(--surface-bg-color-d2)',
      padding: theme.spacing(1),
      borderRadius: '6px',
      overflow: 'auto',
      margin: '8px 0',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        color: 'var(--text-color-main)',
        fontSize: '12px'
      }
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: '8px 0',
      fontSize: 'var(--font-size-sm)',
      '& th, & td': {
        border: '1px solid var(--color-neutral-600)',
        padding: '6px 12px',
        textAlign: 'left'
      },
      '& th': {
        backgroundColor: 'var(--surface-bg-color-d1)',
        fontWeight: 600
      }
    }
  },
  attachments: {
    marginTop: theme.spacing(0.75),
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap'
  },
  attachmentChip: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)',
    padding: '2px 6px',
    borderRadius: '12px',
    backgroundColor: 'var(--surface-bg-color-d1)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px'
  }
}))

function ToolCallList ({ toolCalls, toolResults }) {
  const [expandedIds, setExpandedIds] = useState(new Set())

  const toggleExpanded = (toolCallId) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(toolCallId)) {
        next.delete(toolCallId)
      } else {
        next.add(toolCallId)
      }
      return next
    })
  }

  return (
    <div style={{ marginTop: '8px', width: '100%' }}>
      {toolCalls.map((toolCall, idx) => {
        const toolCallId = toolCall.id || `tool-${idx}`
        const toolResult = toolResults[toolCall.id] || toolResults[toolCall.name] || null
        const isExpanded = expandedIds.has(toolCallId)

        return isExpanded ? (
          <ToolCallExpanded
            key={toolCallId}
            toolCall={toolCall}
            toolResult={toolResult}
            onCollapse={() => toggleExpanded(toolCallId)}
          />
        ) : (
          <ToolCallCollapsed
            key={toolCallId}
            toolCall={toolCall}
            toolResult={toolResult}
            onClick={() => toggleExpanded(toolCallId)}
          />
        )
      })}
    </div>
  )
}

ToolCallList.propTypes = {
  toolCalls: PropTypes.array.isRequired,
  toolResults: PropTypes.object.isRequired
}

function ChatMessage ({ message, onProposePipeline, allMessages, messageIndex }) {
  const classes = useStyles()
  const isUser = message.role === 'user'
  const isAssistant = message.role === 'assistant'
  const isTool = message.role === 'tool'

  const textContent = useMemo(() => {
    if (message.content) {
      if (typeof message.content === 'string') {
        return message.content.replace(/<attachment[^>]*>[\s\S]*?<\/attachment>/g, '').trim()
      }
      if (Array.isArray(message.content)) {
        return message.content
          .filter(item => item.type === 'text')
          .map(item => item.text.replace(/<attachment[^>]*>[\s\S]*?<\/attachment>/g, '').trim())
          .filter(text => text.length > 0)
          .join('\n')
      }
    }
    return message.content || ''
  }, [message.content])

  const renderers = useMemo(() => ({
    pre ({ children }) {
      const codeElement = children.props ? children : children[0]
      const className = codeElement.props?.className || ''
      const codeString = String(codeElement.props?.children || '').replace(/\n$/, '')
      const match = /language-(.*)/.exec(className)
      const language = match ? match[1] : ''

      return (
        <CodeBlockWithActions
          code={codeString}
          language={language}
          onApplyToEditor={onProposePipeline}
        />
      )
    },
    code ({ inline, children, ...props }) {
      return <code {...props}>{children}</code>
    },
    del ({ children, ...props }) {
      return <span {...props} style={{ opacity: 0.7 }}>{children}</span>
    },
    s ({ children, ...props }) {
      return <span {...props} style={{ opacity: 0.7 }}>{children}</span>
    }
  }), [onProposePipeline])

  const attachments = useMemo(() => {
    if (isUser && Array.isArray(message.content)) {
      return message.content
        .filter(item => item.type === 'text' && item.text.includes('<attachment'))
        .map(item => {
          const match = /<attachment[^>]*name="([^"]+)"[^>]*>/.exec(item.text)
          return match ? match[1] : null
        })
        .filter(Boolean)
    }
    return []
  }, [isUser, message.content])

  const toolResults = useMemo(() => {
    const results = {}
    if (isAssistant && allMessages && messageIndex !== undefined) {
      for (let i = messageIndex + 1; i < allMessages.length; i++) {
        const msg = allMessages[i]
        if (msg.role === 'tool') {
          if (msg.toolCallId) {
            results[msg.toolCallId] = msg.content
          } else if (msg.name) {
            results[msg.name] = msg.content
          }
        } else {
          break
        }
      }
    }
    return results
  }, [isAssistant, allMessages, messageIndex])

  if (message.name === 'system') {
    return (
      <div className={`${classes.message}`} style={{ alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.5, textAlign: 'center' }}>
          {textContent}
        </div>
      </div>
    )
  }

  if (isTool) return null

  if (isUser) {
    return (
      <div className={`${classes.message} ${classes.userMessage}`}>
        <div className={`${classes.messageBubble} ${classes.userBubble}`}>
          <div className={classes.content}>
            {textContent}
          </div>
          {attachments.length > 0 && (
            <div className={classes.attachments}>
              {attachments.map((name, idx) => (
                <span key={idx} className={classes.attachmentChip}>
                  📎 {name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (isAssistant) {
    const toolCalls = message.toolCalls || []

    return (
      <div className={`${classes.message} ${classes.assistantMessage}`}>
        <div className={classes.content}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={renderers}
            skipHtml
          >
            {textContent}
          </ReactMarkdown>
        </div>
        {toolCalls.length > 0 && (
          <ToolCallList
            toolCalls={toolCalls}
            toolResults={toolResults}
          />
        )}
      </div>
    )
  }

  return null
}

ChatMessage.propTypes = {
  message: PropTypes.object.isRequired,
  onProposePipeline: PropTypes.func,
  allMessages: PropTypes.array,
  messageIndex: PropTypes.number
}

export default ChatMessage
